import { useState, useCallback, useRef, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Text, Button, Alert, AlertIcon, VStack } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import BankPayoutReview from "./BankPayoutReview";
import NonBankPayoutReview from "./NonBankPayoutReview";
import { useAnalytics } from "utils";

const Confirmation = ({ isSubmitting, submitError }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const { values, isValid, setFieldValue } = useFormikContext();
  const [isPayoutAmountInvalid, setIsPayoutAmountInvalid] = useState(false);
  const handleNonBankPayoutCalculationsLoadedHasBeenCalled = useRef(false);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("PayoutV2", "ConfirmAndSubmit_Viewed", {
        amount: values.amount,
        country: values.payoutDestination?.country_iso || null,
      });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, values]);

  const handleNonBankPayoutCalculationsLoaded = useCallback((payoutCalcs) => {
    if (handleNonBankPayoutCalculationsLoadedHasBeenCalled.current) return; // only needed once, and can trigger a loop since we're setting form values within
    handleNonBankPayoutCalculationsLoadedHasBeenCalled.current = true;
    const { payout_requested, payout_fee, payout_total } = payoutCalcs;
    if(typeof payout_total === "number" && payout_total <= 0) {
      setIsPayoutAmountInvalid(true);
    } else {
      setIsPayoutAmountInvalid(false);
    }
    setFieldValue("payoutAmountDetails", { payout_requested, payout_fee, payout_total }); // track what we're showing user for amounts with non-bank options for analytic event
  }, [setIsPayoutAmountInvalid, setFieldValue]);

  let content;
  if ("bank_account_id" in (values?.payoutDestination || {})) {
    content = <BankPayoutReview values={values} />;
  } else {
    content = <NonBankPayoutReview values={values} onPayoutCalculationsLoaded={handleNonBankPayoutCalculationsLoaded} />;
  }

  let errMsg = null;
  if (submitError) {
    const errCodes = submitError.data?.codes || [];
    if (errCodes.includes("DOCUMENTATION_REQUIRED")) {
      errMsg = (
        <VStack align="flex-start" spacing={0}>
          <FormattedMessage
            defaultMessage="KYC documentation required, please contact support: {link}"
            description="Error message to user with link to reach support."
            values={{
              link: (
                <i>
                  <a href="https://tiliapay.freshdesk.com/support/home" rel="noreferrer" target="_blank">
                    tilia.io/support
                  </a>
                </i>
              ),
            }}
          />
        </VStack>
      );
    } else if (errCodes.includes("INVALID_AMOUNT")) {
      errMsg = (
        <VStack align="flex-start" spacing={0}>
          <FormattedMessage defaultMessage="Invalid amount" description="error message" />
        </VStack>
      );
    } else if (errCodes.includes("INVALID_BANK_DETAILS")) {
      errMsg = (
        <VStack align="flex-start" spacing={0}>
          <FormattedMessage
            defaultMessage="Bank account details are invalid.  Please try adding the account again and make sure all the information is correct."
            description="Error message shown to user when some aspect of the bank account information we have is invalid."
          />
        </VStack>
      );
    } else if (submitError.message) {
      errMsg = submitError.message;
    } else {
      errMsg = <FormattedMessage defaultMessage="There was an error." description="Alert title" />;
    }
  }

  return (
    <Box py="6">
      <Box pb="6">
        <Text fontSize="lg" as="b">
          <FormattedMessage defaultMessage="Confirm and submit" description="Section header" />
        </Text>
      </Box>
      {content}
      <Box pt="8">
        <Button
          size="lg"
          w="100%"
          type="submit"
          isDisabled={!isValid || isSubmitting || isPayoutAmountInvalid}
          isLoading={isSubmitting}
        >
          <FormattedMessage defaultMessage="Submit" description="Button label" />
        </Button>
        {isPayoutAmountInvalid && (
          <Text as="i" color="errorText" fontSize="sm">
            <FormattedMessage defaultMessage="Invalid amount" description="error message" />
          </Text>
        )}
      </Box>
      {submitError && (
        <Box pt="8">
          <Alert status="error">
            <AlertIcon />
            {errMsg}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Confirmation;
